/* Admin — Bookkeeping (Sep-2026).
 *
 * Focused ledger-entry-only surface for the `ledger_entry` ("Ledger
 * clerk") scope — Chart of Accounts + manual journal entries, with
 * none of the P&L/Balance Sheet/AI Consultant/COGS noise a pure
 * data-entry role doesn't need. Approve/reject on entries they post
 * is still blocked server-side (require `ap_approver`/`pro_writer`
 * on `/admin/gl/entries/{id}/approve|reject`), so this view is safe
 * to hand to a bookkeeper with no further gating needed here.
 */
import React, { useEffect } from 'react';
import { Landmark } from 'lucide-react';
import LedgerPanel from './LedgerPanel';
import { startContextualTour } from '@/components/GuidedTour';

// iter-bookkeeper-tour · Sep-2026 — first-login walkthrough for a
// brand-new Ledger Clerk. Shown once per browser (GuidedTour's own
// `hasSeenTour` gate) the first time this panel ever mounts.
const BOOKKEEPING_TOUR_STEPS = [
  { id: 'bk-coa', anchor: 'ledger-chart-of-accounts',
    title: 'Your Chart of Accounts',
    body:  'Accounts are grouped by class — Assets, Liabilities, Equity, Revenue, Expenses — each showing its current balance. Click any account code to filter Recent Entries down to just that account.' },
  { id: 'bk-entry', anchor: 'ledger-new-entry',
    title: 'Post a journal entry',
    body:  'Pick an account and a debit or credit amount on each line. Debits must equal credits exactly before you can post — the totals below update live as you type.' },
  { id: 'bk-submit', anchor: 'ledger-entry-submit',
    title: 'Post entry',
    body:  'This lights up once the entry balances. Once posted it goes to an admin for approval — you can never edit posted history directly, only reverse-and-correct it later.' },
  { id: 'bk-recent', anchor: 'ledger-recent-entries',
    title: 'Recent entries',
    body:  'Every entry you post shows here with its status. Use "Correct" on any of your own entries to create an auditable reversal instead of touching the original.' },
];

export default function BookkeepingPanel() {
  useEffect(() => {
    const t = setTimeout(
      () => startContextualTour('bookkeeping-first-open', BOOKKEEPING_TOUR_STEPS),
      700,
    );
    return () => clearTimeout(t);
  }, []);

  return (
    <section data-testid="admin-bookkeeping-panel" className="space-y-3">
      <div>
        <div className="inline-flex items-center gap-1.5 text-[16px] font-semibold">
          <Landmark className="h-4 w-4" /> Bookkeeping
        </div>
        <div className="text-[11.5px] text-muted-foreground">
          Chart of Accounts and manual ledger entries. Entries you post here go to an admin for approval.
        </div>
      </div>
      <LedgerPanel />
    </section>
  );
}
